/**
 * Transport factory — picks the right SDK Transport for the current mode.
 *
 * The SDK ships the canonical {@link Transport} interface and the
 * {@link TransportOutbound} discriminated union. This file is the seam that
 * decides where the SDK's outbound events go in the current runtime mode:
 *
 *  - `iframe`     → forward into the host-bridge IframeClient (via asTransport)
 *  - `standalone` → POST to the Rails REST API (stubbed until endpoints wire)
 */
import {
  NullTransport,
  type Transport,
  type TransportOutbound,
} from '@rendever/games-sdk';

import type { IframeClient } from '@/host/types';

export type { Transport, TransportOutbound };

/**
 * Iframe transport — delegates straight to the host-bridge IframeClient's
 * SDK adapter. The structural shapes match: SdkTransport accepts any
 * `kind`/`payload`, and SDK's Transport sends concrete TransportOutbound
 * variants — TypeScript's parameter-contravariance lets the adapter satisfy
 * the SDK interface directly.
 */
export function IframeTransport({ client }: { client: IframeClient }): Transport {
  return client.asTransport();
}

export interface HttpTransportOptions {
  apiBase: string;
  fetchImpl?: typeof fetch;
  /** Bearer token for the Rails API (typically the Firebase ID token). */
  getAuthToken?: () => string | null;
}

/**
 * HTTP transport — direct POSTs to the Rails REST API.
 *
 * Stub for now: logs and resolves. Wiring to /community_os/v1/* lands when
 * the Rails endpoints are in place.
 */
export function HttpTransport({
  apiBase,
  fetchImpl,
  getAuthToken,
}: HttpTransportOptions): Transport {
  const f = fetchImpl ?? (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : null);
  return {
    async send(event: TransportOutbound): Promise<void> {
      console.debug('[transport:http] (stub) would POST', { apiBase, event });
      void f;
      void getAuthToken;
    },
  };
}

export interface CreateTransportOptions {
  mode: 'iframe' | 'standalone';
  client: IframeClient | null;
  apiBase?: string;
  getAuthToken?: () => string | null;
}

/**
 * Picks the right transport for the current mode.
 *
 * - `iframe` + a present client → IframeTransport (delegates to client.asTransport())
 * - `standalone` (or iframe without a client) → HttpTransport against
 *   `VITE_API_BASE`, or `NullTransport` if none configured (demo/test).
 */
export function createTransport({
  mode,
  client,
  apiBase,
  getAuthToken,
}: CreateTransportOptions): Transport {
  if (mode === 'iframe' && client) {
    return IframeTransport({ client });
  }
  const base = apiBase ?? ((import.meta.env.VITE_API_BASE as string | undefined) ?? '');
  if (!base) {
    return new NullTransport();
  }
  return HttpTransport({
    apiBase: base,
    ...(getAuthToken ? { getAuthToken } : {}),
  });
}
